import { addIncidentsWeight, sortByPriority } from '@shared/rolling-stocks-update/services/sortService';

import { IncidentTypeEnum, PrefixInfo } from '../../models';
import { CountState } from '../../redux/types';
import { ApiRollingStockResponse, TrackingBoardDTO } from '../dataProviders';

import { dateTimeService } from './dateTime';

export const PREFIXES: {
	[lineId: string]: PrefixInfo;
} = {
	'1': {
		defaultFilter: 'N2-5',
		fullPtrn: /^N2 -5\\d{2}$/,
		maxLength: 7,
		prefixTxt: 'N2 -5',
		startPtrn: /^N2 -5/,
	},
	'2': {
		defaultFilter: 'N30',
		fullPtrn: /^N 30\\d{3}$/,
		maxLength: 7,
		prefixTxt: 'N 30',
		startPtrn: /^N 30/,
	},
	'3': {
		defaultFilter: '',
		fullPtrn: /^NA 12\\d{3}$/,
		maxLength: 8,
		prefixTxt: 'NA 12',
		startPtrn: /^NA 12/,
	},
	'3B': {
		defaultFilter: '',
		fullPtrn: /^B14\\d{3}$/,
		maxLength: 6,
		prefixTxt: 'B14',
		startPtrn: /^B14/,
	},
	'4': {
		defaultFilter: 'CC-',
		fullPtrn: /^CC-\\d{3}$/,
		maxLength: 6,
		prefixTxt: 'CC-',
		startPtrn: /^CC-/,
	},
	'5': {
		defaultFilter: 'N30',
		fullPtrn: /^N 30\\d{3}$/,
		maxLength: 7,
		prefixTxt: 'N 30',
		startPtrn: /^N 30/,
	},
	'6': {
		defaultFilter: 'A65',
		fullPtrn: /^A 65\\d{3}$/,
		maxLength: 7,
		prefixTxt: 'A 65',
		startPtrn: /^A 65/,
	},
	'7': {
		defaultFilter: '',
		fullPtrn: /^NA31\\d{3}$/,
		maxLength: 7,
		prefixTxt: 'NA31',
		startPtrn: /^NA31/,
	},
	'7B': {
		defaultFilter: '',
		fullPtrn: /^B00\\d$/,
		maxLength: 4,
		prefixTxt: 'B00',
		startPtrn: /^B00/,
	},
	'8': {
		defaultFilter: 'NA31',
		fullPtrn: /^NA 31\\d{3}$/,
		maxLength: 8,
		prefixTxt: 'NA 31',
		startPtrn: /^NA 31/,
	},
	'9': {
		defaultFilter: 'NA30',
		fullPtrn: /^N 30\\d{3}$/,
		maxLength: 7,
		prefixTxt: 'N 30',
		startPtrn: /^N 30/,
	},
	// eslint-disable-next-line sort-keys
	'10': {
		defaultFilter: 'NA12',
		fullPtrn: /^NA 12\\d{3}$/,
		maxLength: 8,
		prefixTxt: 'NA 12',
		startPtrn: /^NA 12/,
	},
	'11': {
		defaultFilter: 'A65',
		fullPtrn: /^A65\\d{2}$/,
		maxLength: 5,
		prefixTxt: 'A65',
		startPtrn: /^A65/,
	},
	'12': {
		defaultFilter: 'A12',
		fullPtrn: /^A13\\d{3}$/,
		maxLength: 6,
		prefixTxt: 'A13',
		startPtrn: /^A13/,
	},
	'13': {
		defaultFilter: 'NA31',
		fullPtrn: /^NA 31\\d{3}$/,
		maxLength: 8,
		prefixTxt: 'NA 31',
		startPtrn: /^NA 31/,
	},
	'14': {
		defaultFilter: 'CA-',
		fullPtrn: /^CA-\\d{2}$/,
		maxLength: 5,
		prefixTxt: 'CA-',
		startPtrn: /^CA-/,
	},
	A: {
		defaultFilter: '',
		fullPtrn: /^\\d{4}$`/,
		maxLength: 4,
		prefixTxt: '',
		startPtrn: /^$/,
	},
	B: {
		defaultFilter: '',
		fullPtrn: /^\\d{4,5}$/,
		maxLength: 5,
		prefixTxt: '',
		startPtrn: /^$/,
	},
};

export const calcStatusCount = (
	rollingStocks: TrackingBoardDTO[],
	npHighLimit?: number
): CountState => {
	return rollingStocks.reduce(
		(acc, val) => {
			const incidentsGrNumber = val.incidents.filter(
				(incident) => incident.incidentTypeId === IncidentTypeEnum.GR
			).length;

			const delta = Number(val.NpDeltaTime ?? 0.5); // pas compris
			const hasNpIncident = npHighLimit && delta >= npHighLimit;

			return {
				degrafed: acc.degrafed + incidentsGrNumber,
				toClean: acc.toClean + (hasNpIncident ? 1 : 0),
				total: acc.total + 1,
				used: acc.used + (val.isUsed ? 1 : 0),
			};
		},
		{
			degrafed: 0,
			toClean: 0,
			total: 0,
			used: 0,
		}
	);
};

export const transformToValidTrainCode = (code: string) =>
	code.replace(/\s+/g, '').replace(/^(.*)-0$/, '$1-');

export const RerLines = ['A', 'B'];

export const sortRollingStocksByPriority = (
	rollingStocks: ApiRollingStockResponse,
	transform?: (rollingStock: TrackingBoardDTO) => TrackingBoardDTO
): ApiRollingStockResponse => {
	const processedStocks = transform
		? rollingStocks.map(transform)
		: rollingStocks;

	return processedStocks
		.map(mapToRollingStockForSort)
		.map(addIncidentsWeight)
		.sort(sortByPriority) as unknown as ApiRollingStockResponse;
};

const mapToRollingStockForSort = (rollingStock: TrackingBoardDTO) => ({
	...rollingStock,
	incidents: rollingStock.incidents.map((i) => ({ ...i, isPriority: i.isPriority ?? false })),
	isUsed: rollingStock?.isUsed ?? false,
	isUsedOrder: (rollingStock?.isUsed ?? false) ? 1 : 0,
	lastNPUpdate: rollingStock.NP ?? null,
	...setCleanStatus(rollingStock),
});

export const setCleanStatus = (rollingStock: TrackingBoardDTO) => {
	return {
		MalDeltaTime: dateTimeService.dayDiff(rollingStock.MAL, new Date()),
		NcDeltaTime: dateTimeService.dayDiff(rollingStock.NC, new Date()),
		NcrDeltaTime: dateTimeService.dayDiff(rollingStock.NCR, new Date()),
		NpDeltaTime: dateTimeService.dayDiff(rollingStock.NP, new Date()),
		PbsDeltaTime: dateTimeService.dayDiff(rollingStock.PBS, new Date()),
	};
};
